import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  completeSpaTreatmentExecution,
  getSpaInventoryProducts,
  getSpaTreatmentExecution,
  saveSpaTreatmentExecution,
} from "../APIs/SpaAPI";
import { useTranslation } from "react-i18next";

const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;

const executionRows = (execution) => (execution?.productsUsed || []).map((product) => ({
  productId: Number(product.productId),
  name: product.name || `#${product.productId}`,
  unit: product.unit || "PIECE",
  unitCost: Number(product.unitCost || 0),
  plannedQuantity: Number(product.plannedQuantity || 0),
  actualQuantity: Number(product.actualQuantity || product.plannedQuantity || 0),
}));

function SpaTreatmentCompletionDialog({
  open,
  appointment,
  actualEndAt,
  canViewProducts,
  token,
  tenant,
  onClose,
  onCompleted,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [catalogError, setCatalogError] = useState("");
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [productToAdd, setProductToAdd] = useState("");
  const [completion, setCompletion] = useState(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open || !appointment?.id || !token || !tenant) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    setCatalogError("");
    setProducts([]);
    setRows([]);
    setProductToAdd("");
    setCompletion(null);

    const productRequest = canViewProducts
      ? getSpaInventoryProducts(token, tenant, { signal: controller.signal }).catch((failure) => {
        if (active && failure?.name !== "AbortError") setCatalogError(failure?.message || t("spa.products.catalogLoadError"));
        return [];
      })
      : Promise.resolve([]);

    Promise.all([
      getSpaTreatmentExecution(appointment.id, token, tenant),
      productRequest,
    ]).then(([execution, inventoryProducts]) => {
      if (!active) return;
      setRows(executionRows(execution));
      setProducts(inventoryProducts);
    }).catch((failure) => {
      if (active && failure?.name !== "AbortError") {
        setError(failure?.message || t("spa.products.loadError"));
      }
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [appointment?.id, canViewProducts, open, t, tenant, token]);

  const availableProducts = useMemo(() => {
    const used = new Set(rows.map((row) => Number(row.productId)));
    return products.filter((product) => !used.has(Number(product.id)));
  }, [products, rows]);

  const plannedCost = useMemo(() => rows.reduce((sum, row) => (
    sum + Number(row.plannedQuantity || 0) * Number(row.unitCost || 0)
  ), 0), [rows]);

  const actualCost = useMemo(() => rows.reduce((sum, row) => (
    sum + Number(row.actualQuantity || 0) * Number(row.unitCost || 0)
  ), 0), [rows]);

  const valid = rows.every((row) => Number.isFinite(Number(row.actualQuantity))
    && Number(row.actualQuantity) >= 0
    && Number(row.actualQuantity) <= 100000);

  const changeQuantity = (productId, quantity) => {
    setRows((current) => current.map((row) => (
      Number(row.productId) === Number(productId) ? { ...row, actualQuantity: quantity } : row
    )));
  };

  const addProduct = () => {
    const product = products.find((item) => Number(item.id) === Number(productToAdd));
    if (!product) return;
    setRows((current) => [...current, {
      productId: Number(product.id),
      name: product.name,
      unit: product.unit || "PIECE",
      unitCost: Number(product.unitCost || 0),
      plannedQuantity: 0,
      actualQuantity: 1,
    }]);
    setProductToAdd("");
  };

  const complete = async () => {
    if (!appointment?.id || !valid || inFlight.current) return;
    const responsibleOperatorId = Number(
      appointment.operatorId
      || appointment.execution?.responsibleOperatorId
      || appointment.assignedResources?.operators?.[0]?.id
    );
    if (!responsibleOperatorId) {
      setError(t("spa.operations.operatorRequired"));
      return;
    }

    inFlight.current = true;
    setSaving(true);
    setError("");
    try {
      await saveSpaTreatmentExecution(appointment.id, {
        responsibleOperatorId,
        actualEndAt,
        productsUsed: rows
          .filter((row) => Number(row.actualQuantity) > 0)
          .map((row) => ({ productId: Number(row.productId), quantity: Number(row.actualQuantity) })),
      }, token, tenant);
      const result = await completeSpaTreatmentExecution(appointment.id, {}, token, tenant);
      setCompletion(result);
      await onCompleted?.(result);
    } catch (failure) {
      setError(failure?.message || t("spa.operations.actionError"));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  const alerts = Array.isArray(completion?.inventoryAlerts) ? completion.inventoryAlerts : [];
  const resultExecution = completion?.execution;

  return (
    <Dialog open={open} onClose={() => (!saving ? onClose?.() : null)} fullWidth maxWidth="md">
      <DialogTitle>{t("spa.products.executionTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography color="text.secondary">{t("spa.products.executionHelp")}</Typography>
          {loading ? <Box sx={{ py: 4, display: "grid", placeItems: "center" }}><CircularProgress /></Box> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {catalogError ? <Alert severity="warning">{t("spa.products.catalogLoadError")}</Alert> : null}

          {!loading && !completion ? (
            <>
              {!rows.length ? <Alert severity="info">{t("spa.products.noConsumption")}</Alert> : null}
              {rows.map((row) => (
                <Paper key={row.productId} variant="outlined" sx={{ p: 1.5 }}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Inventory2OutlinedIcon color="action" fontSize="small" />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={800} noWrap>{row.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {money(row.unitCost)} / {t(`spa.products.units.${row.unit}`, { defaultValue: row.unit })}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField fullWidth size="small" disabled label={t("spa.products.plannedQuantity")} value={row.plannedQuantity} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t("spa.products.actualQuantity")}
                        value={row.actualQuantity}
                        inputProps={{ min: 0, max: 100000, step: 0.01 }}
                        onChange={(event) => changeQuantity(row.productId, event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={10} md={3}>
                      <Typography variant="caption" color="text.secondary">{t("spa.products.actualCost")}</Typography>
                      <Typography fontWeight={800}>{money(Number(row.actualQuantity || 0) * row.unitCost)}</Typography>
                    </Grid>
                    <Grid item xs={2} md={1} sx={{ textAlign: "right" }}>
                      <Tooltip title={t("spa.products.removeProduct")}>
                        <span>
                          <IconButton
                            aria-label={`${t("spa.products.removeProduct")} ${row.name}`}
                            color="error"
                            disabled={Number(row.plannedQuantity) > 0}
                            onClick={() => setRows((current) => current.filter((item) => item.productId !== row.productId))}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              {canViewProducts && availableProducts.length ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                  <TextField
                    select
                    size="small"
                    label={t("spa.products.product")}
                    value={productToAdd}
                    onChange={(event) => setProductToAdd(event.target.value)}
                    sx={{ minWidth: { sm: 280 } }}
                  >
                    {availableProducts.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name} ({t(`spa.products.units.${product.unit}`, { defaultValue: product.unit })})
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" startIcon={<AddIcon />} disabled={!productToAdd} onClick={addProduct}>
                    {t("spa.products.addProduct")}
                  </Button>
                </Stack>
              ) : null}

              {!valid ? <Alert severity="warning">{t("spa.products.invalidActualQuantity")}</Alert> : null}
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{t("spa.products.plannedCost")}</Typography>
                    <Typography variant="h6" fontWeight={900}>{money(plannedCost)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{t("spa.products.actualCost")}</Typography>
                    <Typography variant="h6" fontWeight={900}>{money(actualCost)}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          ) : null}

          {completion ? (
            <Stack spacing={1.5}>
              <Alert severity={completion.idempotent ? "info" : "success"}>
                {t(completion.idempotent ? "spa.products.idempotent" : "spa.products.completed")}
              </Alert>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{t("spa.products.plannedCost")}</Typography>
                    <Typography variant="h6" fontWeight={900}>{money(resultExecution?.plannedProductCost)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{t("spa.products.actualCost")}</Typography>
                    <Typography variant="h6" fontWeight={900}>{money(resultExecution?.actualProductCost)}</Typography>
                  </Paper>
                </Grid>
              </Grid>
              {alerts.map((alert, index) => (
                <Alert severity="warning" key={`${alert.product?.id || alert.product?.name || index}:${alert.location?.id || index}`}>
                  {t("spa.products.negativeStock", {
                    product: alert.product?.name || `#${alert.product?.id || "-"}`,
                    location: alert.location?.name || `#${alert.location?.id || "-"}`,
                    quantity: alert.quantity,
                    unit: t(`spa.products.units.${alert.unit}`, { defaultValue: alert.unit || "" }),
                  })}
                </Alert>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {completion ? (
          <Button variant="contained" onClick={onClose}>{t("spa.products.close")}</Button>
        ) : (
          <>
            <Button disabled={saving} onClick={onClose}>{t("spa.common.cancel")}</Button>
            <Button variant="contained" color="success" disabled={loading || saving || Boolean(error) || !valid} onClick={complete}>
              {saving ? t("spa.common.saving") : t("spa.products.complete")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default SpaTreatmentCompletionDialog;
